import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Link,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { MdCheckCircle, MdOutlineRadioButtonUnchecked } from "react-icons/md";
import confetti from "canvas-confetti";
import { authService } from "../services/authService";
import { restaurantService } from "../services/restaurantService";
import { getBusinessSlug } from "../utils/slugify";

const TASK_ORDER = ["menu", "floorPlan", "employees", "campaign", "profilePublic"];

function onboardingIncompleteStorageKey(restaurantId) {
  return `menom_onboarding_had_incomplete_${restaurantId}`;
}

/** Kurulum tamamlanınca kısa konfeti (navbar üstünde görünsün diye yüksek z-index) */
function fireOnboardingConfetti() {
  if (typeof window === "undefined") return;
  const zIndex = 12000;
  const colors = ["#3182CE", "#38B2AC", "#ECC94B", "#ED64A6", "#805AD5", "#48BB78"];

  const burst = (originX, originY, particleCount, spread = 88) => {
    confetti({
      particleCount,
      spread,
      startVelocity: 38,
      ticks: 240,
      gravity: 1.05,
      decay: 0.91,
      scalar: 1.05,
      origin: { x: originX, y: originY },
      zIndex,
      colors,
    });
  };

  burst(0.5, 0.52, 130, 100);
  window.setTimeout(() => burst(0.2, 0.68, 55, 72), 180);
  window.setTimeout(() => burst(0.8, 0.68, 55, 72), 320);
  window.setTimeout(() => burst(0.5, 0.58, 70, 360), 500);
}

function buildTasks(base) {
  return [
    {
      id: "menu",
      title: "Menü oluşturun",
      hint: "En az bir ürün ekleyin.",
      to: `${base}/menu`,
    },
    {
      id: "floorPlan",
      title: "Masa planı ekleyin",
      hint: "Kat ve en az bir masa tanımlayın (İşletmem sayfası).",
      to: `${base}/isletmem`,
    },
    {
      id: "employees",
      title: "Çalışan ekleyin",
      hint: "Panel kullanacak personel hesabı oluşturun.",
      to: `${base}/isletmem`,
    },
    {
      id: "campaign",
      title: "Kampanya oluşturun",
      hint: "İlk kampanyanızı oluşturun.",
      to: `${base}/kampanyalar`,
    },
    {
      id: "profilePublic",
      title: "Profilinizi tamamlayın",
      hint: "Profil ayarlarından çalışma saatleri, iletişim ve görünürlük bilgilerinizi güncelleyin.",
      to: `${base}/profil-ayarlari`,
    },
  ];
}

export default function GettingStartedChecklist() {
  const location = useLocation();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [checklist, setChecklist] = useState(null);
  const [profileReminderActive, setProfileReminderActive] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const celebrationFiredRef = useRef(false);

  const border = useColorModeValue("gray.200", "whiteAlpha.300");
  const bg = useColorModeValue("gray.50", "rgba(255,255,255,0.04)");
  const doneColor = useColorModeValue("green.600", "green.400");
  const pendingTitle = useColorModeValue("navy.700", "white");
  const doneTitle = useColorModeValue("gray.600", "whiteAlpha.700");

  const fetchChecklist = useCallback(async () => {
    if (!authService.isAuthenticated() || !authService.isManager()) {
      setLoading(false);
      setChecklist(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    try {
      const res = await restaurantService.getRestaurantDetails();
      if (cancelled) return;
      setChecklist(res?.data?.onboardingChecklist || null);
      setProfileReminderActive(res?.data?.profileCompletionReminder === true);
    } catch {
      if (!cancelled) setChecklist(null);
    } finally {
      if (!cancelled) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChecklist();
  }, [location.pathname, fetchChecklist]);

  /** Bu oturumda bir kez eksik görüldüyse, hepsi tamamlanınca tek toast (Strict Mode için id ile tekilleştirme) */
  useEffect(() => {
    if (loading || !checklist) return;
    const rid = typeof window !== "undefined" ? localStorage.getItem("restaurantId") : null;
    if (!rid) return;
    const allDone = TASK_ORDER.every((k) => checklist[k]);
    const key = onboardingIncompleteStorageKey(rid);
    try {
      if (!allDone) {
        sessionStorage.setItem(key, "1");
        celebrationFiredRef.current = false;
        return;
      }
      if (sessionStorage.getItem(key) === "1" && !celebrationFiredRef.current) {
        celebrationFiredRef.current = true;
        fireOnboardingConfetti();
        toast({
          id: `onboarding-all-done-${rid}`,
          title: "Tebrikler, kurulum tamamlandı",
          description: "Başlangıç adımlarını tamamladınız. İyi çalışmalar dileriz.",
          status: "success",
          duration: 7000,
          isClosable: true,
          position: "top",
        });
        sessionStorage.removeItem(key);
      }
    } catch (_) {
      /* sessionStorage kapalı olabilir */
    }
  }, [checklist, loading, toast]);

  if (loading) return null;
  if (!checklist) return null;
  const allDone = TASK_ORDER.every((k) => checklist[k]);
  if (allDone) return null;

  const slug = getBusinessSlug() || "panel";
  const base = `/admin/${slug}`;
  const tasks = buildTasks(base);
  const doneCount = TASK_ORDER.filter((k) => checklist[k]).length;
  const total = TASK_ORDER.length;

  const onDismissProfile = async () => {
    setDismissing(true);
    try {
      await authService.dismissProfileReminder();
      setChecklist((prev) => (prev ? { ...prev, profilePublic: true } : prev));
      setProfileReminderActive(false);
    } catch (e) {
      console.error(e);
    } finally {
      setDismissing(false);
    }
  };

  return (
    <Box
      w="100%"
      mb={5}
      mt={5}
      borderRadius="md"
      borderWidth="1px"
      borderColor={border}
      bg={bg}
      px={{ base: 3, md: 4 }}
      py={3}
    >
      <Flex align="baseline" justify="space-between" gap={2} flexWrap="wrap" mb={2}>
        <Text fontWeight="700" fontSize="md">
          En başta yapılacaklar
        </Text>
        <Text fontSize="sm" color="gray.500">
          {doneCount} / {total} tamamlandı
        </Text>
      </Flex>
      <Text fontSize="xs" color="gray.500" mb={3}>
        Kurulumu tamamlamanız için önerilen adımlar. İlgili sayfada işlemi yaptığınızda otomatik işaretlenir.
      </Text>
      <VStack align="stretch" spacing={3}>
        {tasks.map((task, idx) => {
          const done = checklist[task.id];
          const isLast = idx === tasks.length - 1;
          return (
            <HStack
              key={task.id}
              align="flex-start"
              spacing={3}
              pb={isLast ? 0 : 3}
              borderBottomWidth={isLast ? 0 : "1px"}
              borderColor={border}
            >
              <Icon
                as={done ? MdCheckCircle : MdOutlineRadioButtonUnchecked}
                boxSize={5}
                mt="2px"
                shrink={0}
                color={done ? doneColor : "gray.400"}
              />
              <Box flex="1" minW={0}>
                <Link
                  as={RouterLink}
                  to={task.to}
                  fontSize="sm"
                  fontWeight={done ? "500" : "600"}
                  color={done ? doneTitle : pendingTitle}
                  textDecoration={done ? "none" : "underline"}
                  _hover={{ color: "brand.500" }}
                >
                  {task.title}
                </Link>
                <Text fontSize="xs" color="gray.500" mt={0.5}>
                  {task.hint}
                </Text>
                {task.id === "profilePublic" &&
                  !checklist.profilePublic &&
                  profileReminderActive && (
                    <Button
                      variant="link"
                      size="xs"
                      colorScheme="gray"
                      mt={1}
                      isLoading={dismissing}
                      onClick={onDismissProfile}
                    >
                      Şimdilik hatırlatıcıyı kapat
                    </Button>
                  )}
              </Box>
            </HStack>
          );
        })}
      </VStack>
    </Box>
  );
}
